import { type ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  RequiredVideo,
  RequiredVideoCaption,
  RequiredVideoPlayer,
} from '@/components/ui';
import { GlowOrb } from '@/components/effects/glow-orb';
import {
  useIdentityBlock,
  activeBlockExpiresAt,
} from '@/hooks/use-identity-block';
import { useCountdown } from '@/hooks/use-countdown';

const INTRO_VIDEO_ID = '7914ac68ea7a424b8bb882d13e4065ce';

function BlockedCta({ expiresAt }: { expiresAt: string }): ReactNode {
  const cd = useCountdown(expiresAt);
  if (!cd || cd.expired) return null;
  return (
    <div className="space-y-sm">
      <Button size="lg" className="w-full" disabled>
        Repetir el test en {cd.label}
      </Button>
      <p className="text-center text-xs text-text-tertiary">
        suspendiste el primer intento
      </p>
    </div>
  );
}

function ReadyCta({
  ready,
  onStart,
}: {
  ready: boolean;
  onStart: () => void;
}): ReactNode {
  return (
    <div className="space-y-xs">
      <Button size="lg" className="w-full" disabled={!ready} onClick={onStart}>
        Cualificarme
      </Button>
      <RequiredVideoCaption
        lockedLabel={(t) => <>Te podr&aacute;s cualificar en {t}</>}
      />
    </div>
  );
}

function VideoBlock({
  blockedExpiresAt,
  onStart,
}: {
  blockedExpiresAt: string | undefined;
  onStart: () => void;
}): ReactNode {
  const [ready, setReady] = useState(false);
  return (
    <RequiredVideo
      localKey="intro"
      provider="loom"
      videoId={INTRO_VIDEO_ID}
      threshold={0.99}
      onWatched={() => setReady(true)}
    >
      <RequiredVideoPlayer />
      {blockedExpiresAt ? (
        <BlockedCta expiresAt={blockedExpiresAt} />
      ) : (
        <ReadyCta ready={ready} onStart={onStart} />
      )}
    </RequiredVideo>
  );
}

export function RegistrationLandingPage(): ReactNode {
  const navigate = useNavigate();
  const state = useIdentityBlock();
  const blockedExpiresAt = activeBlockExpiresAt(state);
  return (
    <div className="relative flex min-h-dvh flex-col items-center justify-center bg-void px-md py-lg sm:px-base sm:py-xl">
      <GlowOrb />
      <div className="relative z-10 w-full max-w-[640px] space-y-md text-center sm:max-w-[720px] sm:space-y-lg lg:max-w-[820px]">
        <h1 className="font-display text-xl font-bold tracking-tight text-text-primary sm:text-2xl">
          Espera, &iquest;de verdad eres un jugador?
        </h1>
        <VideoBlock
          blockedExpiresAt={blockedExpiresAt}
          onStart={() => navigate('/register/test')}
        />
      </div>
    </div>
  );
}
